import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiFetch,
  postJSON,
  type BusinessInfo,
  type TemplateCategory,
  type TemplateSubcategory,
  type Site,
  type StoreCategory,
  type Template,
  type TemplateDetail,
} from "./api";

/** Catalog filter: a website category and/or sub-category; omit for everything. */
export interface TemplateFilter {
  category?: TemplateCategory;
  subcategory?: TemplateSubcategory;
}

/** Create-site payload — mirrors POST /sites (OpenAPI createSite). */
export interface CreateSiteInput {
  name: string;
  slug?: string;
  templateId: string;
  business?: BusinessInfo;
  /** Required for store templates: what the store sells. */
  store?: { category: StoreCategory };
}

/** Site list summaries for the dashboard. */
export function useSites() {
  return useQuery({
    queryKey: ["sites"],
    queryFn: () => apiFetch<{ sites: Site[] }>("/sites"),
    select: (data) => data.sites,
  });
}

/** Catalog templates, optionally filtered by category or sub-category. */
export function useTemplates(filter: TemplateFilter = {}) {
  const params = new URLSearchParams();
  if (filter.category) params.set("category", filter.category);
  if (filter.subcategory) params.set("subcategory", filter.subcategory);
  const query = params.size ? `?${params}` : "";
  return useQuery({
    queryKey: ["templates", filter.category ?? "", filter.subcategory ?? ""],
    queryFn: () =>
      apiFetch<{ templates: Template[] }>(`/catalog/templates${query}`),
    select: (data) => data.templates,
  });
}

/** One template with its preview document (sample content substituted). */
export function useTemplate(templateId?: string) {
  return useQuery({
    queryKey: ["template", templateId],
    queryFn: () =>
      apiFetch<TemplateDetail>(`/catalog/templates/${templateId}`),
    enabled: Boolean(templateId),
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiteInput) => postJSON<Site>("/sites", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sites"] }),
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { siteId: string; name: string }) =>
      apiFetch<Site>(`/sites/${input.siteId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: input.name }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sites"] }),
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) =>
      apiFetch<void>(`/sites/${siteId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sites"] }),
  });
}
